import { Html, useKeyboardControls } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Maximize,
  Minimize,
  Pause,
  Play,
} from "lucide-react";
import React, { RefObject, useCallback, useEffect, useRef } from "react";
import { useFullscreen, useToggle } from "react-use";
import { Vector3 } from "three";

type ControlAction = {
  forward?: boolean | undefined | null;
  backward?: boolean | undefined | null;
  left?: boolean | undefined | null;
  right?: boolean | undefined | null;
  up?: boolean | undefined | null;
  down?: boolean | undefined | null;
  rotateLeft?: boolean | undefined | null;
  rotateRight?: boolean | undefined | null;
  rotateUp?: boolean | undefined | null;
  rotateDown?: boolean | undefined | null;
  space?: boolean | undefined | null;
};

export const keyboardControlsMapping: {
  name: keyof ControlAction;
  keys: string[];
}[] = [
  { name: "forward", keys: ["q", "Q"] },
  { name: "backward", keys: ["e", "E"] },
  { name: "up", keys: ["w", "W"] },
  { name: "down", keys: ["s", "S"] },
  { name: "left", keys: ["a", "A"] },
  { name: "right", keys: ["d", "D"] },
  { name: "rotateLeft", keys: ["ArrowLeft"] },
  { name: "rotateRight", keys: ["ArrowRight"] },
  { name: "rotateUp", keys: ["ArrowUp"] },
  { name: "rotateDown", keys: ["ArrowDown"] },
  { name: "space", keys: ["Space"] },
] as const;

function handleControls({
  camera,
  action,
  togglePlay,
  spacePressedRef,
}: {
  camera: THREE.Camera;
  action: ControlAction;
  togglePlay: () => void;
  spacePressedRef: React.MutableRefObject<boolean>;
}) {
  const moveSpeed = 0.5;
  const rotSpeed = 0.02;
  if (action.forward) camera.translateY(moveSpeed);
  if (action.backward) camera.translateY(-moveSpeed);
  if (action.left) camera.translateX(-moveSpeed);
  if (action.right) camera.translateX(moveSpeed);
  if (action.up) camera.translateZ(-moveSpeed);
  if (action.down) camera.translateZ(moveSpeed);

  const zAxis = new Vector3(0, 0, 1);
  if (action.rotateLeft) camera.rotateOnWorldAxis(zAxis, rotSpeed);
  if (action.rotateRight) camera.rotateOnWorldAxis(zAxis, -rotSpeed);
  if (action.rotateUp) camera.rotateX(rotSpeed);
  if (action.rotateDown) camera.rotateX(-rotSpeed);

  if (action.space && !spacePressedRef.current) {
    togglePlay();
    spacePressedRef.current = true;
  } else if (!action.space) {
    spacePressedRef.current = false;
  }

  camera.updateMatrixWorld();
}

export function SceneControls({
  onChange,
  frameCount,
  currentFrame,
  onPlay,
  onStop,
  togglePlay,
  playing,
  containerRef,
  loading,
}: {
  onChange: (frame: number) => void;
  frameCount: number;
  currentFrame: number;
  onPlay: () => void;
  onStop: () => void;
  togglePlay: () => void;
  playing: boolean;
  containerRef: RefObject<HTMLCanvasElement | null>;
  loading: boolean;
}) {
  const { camera } = useThree();

  const spacePressedRef = useRef(false);

  const [show, toggle] = useToggle(false);

  const heldRef = useRef<ControlAction | null>(null);

  useEffect(() => {
    let frame: number;
    const loop = () => {
      if (heldRef.current) {
        handleControls({
          camera,
          action: heldRef.current,
          togglePlay,
          spacePressedRef,
        });
      }
      frame = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(frame);
  }, [camera, togglePlay]);

  const handleHoldStart = (action: ControlAction) => {
    heldRef.current = action;
  };

  const handleHoldEnd = () => {
    heldRef.current = null;
  };

  // @ts-expect-error nullable is required to initialize ref
  const isFullscreen = useFullscreen(containerRef, show, {
    onClose: () => toggle(false),
  });

  const position = new Vector3(
    camera.position.x,
    camera.position.y,
    camera.position.z,
  );

  const onPrevious = useCallback(() => {
    if (playing) {
      return;
    }

    if (currentFrame == 0) {
      onChange(frameCount - 1);
      return;
    }
    onChange(currentFrame - 1);
  }, [currentFrame, frameCount, onChange, playing]);

  const onNext = useCallback(() => {
    if (playing) {
      return;
    }

    if (currentFrame == frameCount - 1) {
      onChange(0);
      return;
    }
    onChange(currentFrame + 1);
  }, [currentFrame, frameCount, onChange, playing]);

  const onChangeRange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(Number(e.target.value) - 1);
    },
    [onChange],
  );

  const [, get] = useKeyboardControls();

  useFrame(({ camera }) => {
    handleControls({ camera, action: get(), togglePlay, spacePressedRef });
  });

  return (
    <Html
      position={position}
      transform={false}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 10,
      }}
    >
      <div className="controls">
        <div className="controls__moving">
          <button
            onPointerDown={() => handleHoldStart({ forward: true })}
            onPointerUp={handleHoldEnd}
            onPointerLeave={handleHoldEnd}
          >
            Q
          </button>
          <button
            onPointerDown={() => handleHoldStart({ up: true })}
            onPointerUp={handleHoldEnd}
            onPointerLeave={handleHoldEnd}
          >
            W
          </button>
          <button
            onPointerDown={() => handleHoldStart({ backward: true })}
            onPointerUp={handleHoldEnd}
            onPointerLeave={handleHoldEnd}
          >
            E
          </button>
          <button
            onPointerDown={() => handleHoldStart({ left: true })}
            onPointerUp={handleHoldEnd}
            onPointerLeave={handleHoldEnd}
          >
            A
          </button>
          <button
            onPointerDown={() => handleHoldStart({ down: true })}
            onPointerUp={handleHoldEnd}
            onPointerLeave={handleHoldEnd}
          >
            S
          </button>
          <button
            onPointerDown={() => handleHoldStart({ right: true })}
            onPointerUp={handleHoldEnd}
            onPointerLeave={handleHoldEnd}
          >
            D
          </button>
        </div>

        <div className="controls__frame">
          {!playing && loading && (
            <button className="loading-indicator">
              <Loader2 size={18} />
            </button>
          )}
          {!playing && !loading && (
            <button className="play-button" onClick={onPlay}>
              <Play size={18} />
            </button>
          )}
          {playing && (
            <button className="stop-button" onClick={onStop}>
              <Pause size={18} />
            </button>
          )}
          <div className="controls__frame__indicator">
            {currentFrame + 1}/{frameCount}
          </div>
          {!loading && (
            <div className="controls__frame__discrete">
              <button onClick={onPrevious}>
                <ChevronLeft size={18} />
              </button>
              <button onClick={onNext}>
                <ChevronRight size={18} />
              </button>
            </div>
          )}
          <div className="controls__frame__input">
            {!loading && (
              <input
                type="range"
                min={1}
                max={frameCount}
                value={currentFrame + 1}
                onChange={onChangeRange}
              />
            )}
          </div>
        </div>

        <div className="controls__rotating">
          <div>
            <button
              onPointerDown={() => handleHoldStart({ rotateUp: true })}
              onPointerUp={handleHoldEnd}
              onPointerLeave={handleHoldEnd}
            >
              <ArrowUp size={18} />
            </button>
          </div>
          <div>
            <button
              onPointerDown={() => handleHoldStart({ rotateLeft: true })}
              onPointerUp={handleHoldEnd}
              onPointerLeave={handleHoldEnd}
            >
              <ArrowLeft size={18} />
            </button>
            <button
              onPointerDown={() => handleHoldStart({ rotateDown: true })}
              onPointerUp={handleHoldEnd}
              onPointerLeave={handleHoldEnd}
            >
              <ArrowDown size={18} />
            </button>
            <button
              onPointerDown={() => handleHoldStart({ rotateRight: true })}
              onPointerUp={handleHoldEnd}
              onPointerLeave={handleHoldEnd}
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        <div className="controls__fullscreen">
          {!isFullscreen && (
            <button onClick={() => toggle()}>
              {" "}
              <Maximize size={18} />
            </button>
          )}
          {isFullscreen && (
            <button onClick={() => toggle()}>
              {" "}
              <Minimize size={18} />
            </button>
          )}
        </div>
      </div>
    </Html>
  );
}
